import { useFlag } from '@calendly/feature-check-react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { useGetOrganizationUuid } from 'common'
import { SearchMeetingDetailContext } from 'components/meetings/meeting_detail/meeting_detail_context'
import { getDashboardEventIdForInvitees } from 'components/meetings/utils/get_dashboard_event_id_for_invitees'
import { useRightRail } from 'features/right_rail'
import { timezoneSelector } from 'selectors/current_user'
import { isUuidLike } from 'shared/utils/uuid'

import { useGetMeetingQuery } from '../api'
import {
  useGetMeetingQuery as useGetMeetingFromSearchQuery,
  useGetMeetingSupergraphQuery,
} from '../graphql_api'
import { mapNodeToMeeting } from '../utils'

/** Grace period (ms) for uuid-only dashboard id before allowing invitees; see restCalendlyPayloadVersion. */
const INVITEES_DASH_UUID_ONLY_GRACE_MS = 100

const dashEventIdForInviteesFromMerged = (merged) => (
  getDashboardEventIdForInvitees({ ...merged, inviteesDashEventId: undefined })
)

const useGetMeeting = (meetingId) => {
  const lastSelectionRef = useRef(null)
  const timezone = useSelector(timezoneSelector)
  const hasMsTeamsFlag = useFlag('ms_teams_notetaker')
  const useSupergraphForDetail = useFlag('sa_meetings_page_with_supergraph', false)
  const organizationUuid = useGetOrganizationUuid()

  const { data: searchGqlData, isFetching: isSearchFetching, isError: isSearchError } = useGetMeetingFromSearchQuery(
    { uuid: meetingId },
    { skip: !meetingId || useSupergraphForDetail }
  )

  const { data: supergraphDetail, isFetching: isSupergraphFetching, isError: isSuperError } = useGetMeetingSupergraphQuery(
    { id: meetingId, organizationUuid: organizationUuid || '' },
    { skip: !meetingId || !useSupergraphForDetail || !organizationUuid }
  )

  const graphDetail = useSupergraphForDetail ? supergraphDetail : searchGqlData
  const calendlyRestEventId = useMemo(() => {
    if (graphDetail?.kind !== 'CALENDLY' || !graphDetail?.uuid) return meetingId
    return graphDetail.uuid
  }, [graphDetail, meetingId])

  const {
    data,
    isFetching: isRestMeetingsQueryFetching,
    isError,
    isUninitialized: isRestGetMeetingUninitialized
  } = useGetMeetingQuery({
    eventId: calendlyRestEventId,
    selectedView: 'scheduled',
    scopeType: 'all_users_and_teams',
  }, { skip: !meetingId || graphDetail?.kind !== 'CALENDLY' })

  const calendlyRestMergeRef = useRef({ uuid: null, merged: null })
  const [restCalendlyPayloadVersion, setRestCalendlyPayloadVersion] = useState(0)

  useLayoutEffect(() => {
    if (lastSelectionRef.current === meetingId) return
    lastSelectionRef.current = meetingId
    calendlyRestMergeRef.current = { uuid: null, merged: null }
  }, [meetingId])

  useLayoutEffect(() => {
    if (!graphDetail) return
    if (graphDetail.kind !== 'CALENDLY') {
      calendlyRestMergeRef.current = { uuid: null, merged: null }
      return
    }
    if (!data) return

    const mUuid = data.uuid
    if (mUuid && calendlyRestMergeRef.current.uuid !== mUuid) {
      calendlyRestMergeRef.current = { uuid: mUuid, merged: { ...data } }
      return
    }

    const prev = calendlyRestMergeRef.current
    calendlyRestMergeRef.current = {
      uuid: mUuid || prev?.uuid,
      merged: { ...(prev?.merged || {}), ...data }
    }
  }, [graphDetail, data])

  useLayoutEffect(() => {
    setRestCalendlyPayloadVersion(0)
  }, [meetingId])

  useLayoutEffect(() => {
    if (graphDetail?.kind === 'CALENDLY' && data) {
      setRestCalendlyPayloadVersion((n) => n + 1)
    }
  }, [data, graphDetail?.kind])

  // Legacy path waits on search GQL; supergraph path must wait on getMeetingById. Otherwise the
  // rest + invitees layer could run in the same timing gap the GQL path never exposed.
  const isGraphLayerPending = useSupergraphForDetail
    ? isSupergraphFetching
    : isSearchFetching

  const isRestGetMeetingInFlight = graphDetail?.kind === 'CALENDLY'
    && (isGraphLayerPending
      || isRestGetMeetingUninitialized
      || isRestMeetingsQueryFetching
      || !data)

  let isDashIdUuidOnly = false
  if (graphDetail?.kind === 'CALENDLY') {
    const merged = calendlyRestMergeRef.current?.merged
    if (merged) {
      const dash = dashEventIdForInviteesFromMerged(merged)
      isDashIdUuidOnly = dash != null && isUuidLike(String(dash))
    }
  }

  const [allowUuidOnlyDash, setAllowUuidOnlyDash] = useState(false)

  useLayoutEffect(() => {
    let t
    if (graphDetail?.kind !== 'CALENDLY') {
      setAllowUuidOnlyDash(true)
    } else if (isRestGetMeetingInFlight) {
      setAllowUuidOnlyDash(false)
    } else if (!isDashIdUuidOnly) {
      setAllowUuidOnlyDash(true)
    } else if (restCalendlyPayloadVersion >= 2) {
      setAllowUuidOnlyDash(true)
    } else {
      setAllowUuidOnlyDash(false)
      t = setTimeout(() => {
        setAllowUuidOnlyDash(true)
      }, INVITEES_DASH_UUID_ONLY_GRACE_MS)
    }
    return () => {
      if (t) clearTimeout(t)
    }
  }, [
    graphDetail?.kind,
    isRestGetMeetingInFlight,
    isDashIdUuidOnly,
    restCalendlyPayloadVersion
  ])

  const dashboardInviteeFetchUnblocked = useMemo(() => {
    if (graphDetail?.kind !== 'CALENDLY') return true
    if (isRestGetMeetingInFlight) return false
    if (!isDashIdUuidOnly) return true
    if (restCalendlyPayloadVersion >= 2) return true
    return allowUuidOnlyDash
  }, [graphDetail?.kind, isRestGetMeetingInFlight, isDashIdUuidOnly, restCalendlyPayloadVersion, allowUuidOnlyDash])

  const meeting = useMemo(() => {
    if (!graphDetail) return null

    if (graphDetail?.kind === 'CALENDLY') {
      if (!data) return null
      // Force recalculation when REST merge ref accumulates without changing `data` reference.
      if (restCalendlyPayloadVersion === -1) return null
      const merged = calendlyRestMergeRef.current?.merged ?? { ...data }
      return {
        ...merged,
        inviteesDashEventId: dashEventIdForInviteesFromMerged(merged)
      }
    }

    return mapNodeToMeeting(graphDetail, timezone, hasMsTeamsFlag)
  }, [graphDetail, data, timezone, hasMsTeamsFlag, restCalendlyPayloadVersion])

  return {
    meeting,
    isFetching: isSearchFetching || isSupergraphFetching || isRestMeetingsQueryFetching,
    isError: isSearchError || isSuperError || isError,
    dashboardInviteeFetchUnblocked
  }
}

export { SearchMeetingDetailContext }

export const SearchMeetingProvider = ({ selectedMeetingId, children }) => {
  const { meeting, isFetching, isError, dashboardInviteeFetchUnblocked } = useGetMeeting(selectedMeetingId)
  const { close } = useRightRail()

  const context = useMemo(() => ({
    selectedEvent: meeting,
    isLoading: isFetching,
    handleClose: close,
    isError,
    dashboardInviteeFetchUnblocked
  }), [meeting, isFetching, isError, close, dashboardInviteeFetchUnblocked])

  return (
    <SearchMeetingDetailContext.Provider value={context}>
      {children}
    </SearchMeetingDetailContext.Provider>
  )
}
